import { Box, Stack, Typography } from '@mui/material';
import { ICertificateFullInfo } from '@repo/types';
import { UserPreview } from 'components/UI';
import { formatedDate } from 'utils/date';

/**
 * VERA-304 — the recipient, through the SHARED person row, with the date on its own axis.
 *
 * This is the SENDER's mirror of `GiftInInfoSection`, and it is now built the same way.
 * It previously hand-rolled the `@repo/shadcn-ui` Avatar with
 * `primaryText`/`secondaryText`/`descriptionText`, which stacked name → date → sentence
 * in one column and reproduced the two defects the gift-in card had already been moved
 * off (§28/§36 — the same unit, hand-rolled a second time):
 *
 *  1. The HANDLE could not appear at all. `getNameOrEmailSlug` collapses name and handle
 *     into ONE string, so a recipient with both showed only one of them, and a
 *     handle-only recipient showed `@spencer2` as their title with no sub-line.
 *     `UserPreview` splits them (`resolveUserIdentity`: name on top, `@handle` under it),
 *     which is what the gift FORM and the recipient picker already render — so the person
 *     you picked and the person the card reports now read identically.
 *  2. The DATE sat in `secondaryText`, i.e. in the person's own sub-line, as if it were
 *     part of their identity — which is also what displaced the handle. It is a property
 *     of the GIFT, so it moves out of the person row and right-aligns against it: the
 *     same left-label / right-value axis `GiftInInfoSection` and every row of
 *     `OwnerProvenanceCard` use.
 *
 * The unclaimed sentence moves OUT of the person column too, for the same reason and one
 * more: it describes the gift's STATE, not the recipient, and indenting it to the name
 * gave a full-width sentence a 52px left margin that aligned with nothing else in the
 * card. Flush left it shares an edge with the heading, the avatar and the Cancel button.
 */
export const GiftOutInfoSection = ({ certificate }: { certificate: ICertificateFullInfo }) => {
  const { toCustomer } = certificate.transfer ?? {};

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1.5}>
        {/* `UserPreview` renders avatar + name + sub-line as siblings, so the row wrapper
            (and its gap) belongs to the caller — its own contract.

            Passing the RECORD rather than a pre-resolved string is the point: it routes
            the label through the one shared resolver, so this card can never disagree
            with the picker about what to call the same person. The email last-resort is
            reachable here and is correct for it — the invite-by-email case is a person
            with no account, whose address the SENDER typed themselves and is reading back
            on their own screen, which is exactly the condition the resolver documents as
            the one place that step is defensible. */}
        <UserPreview user={toCustomer} avatarUrl={toCustomer?.avatar} />
        {/* Top-aligned to the NAME line rather than centred on the whole two-line block,
            so a recipient with no handle (one line) and one with a handle (two) put the
            date in the same place. */}
        <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {formatedDate({
              date: certificate.transfer?.created_at,
              /*
               * DATE ONLY — the same format `GiftInInfoSection` takes, and for the reason
               * that card's own note gives: `MMM do yyyy, h:mm a` is 22 characters, and
               * once the date shares a row with the person's name on a 375px phone the
               * two no longer fit. Measured at the card's ~309px of content: avatar and
               * gap take 52, "Spencer Stebbins" at 16/500 takes ~130, and the full
               * timestamp ~150 — 280 into 257, so the name (which is `noWrap`) would
               * ellipsise. At `MMM do yyyy` the date is ~92 and the pair fits.
               *
               * That card's note explicitly exempted THIS file, on the grounds that the
               * date here sat on its own line "where it competes with nothing and has no
               * wrap pressure". That was true of the stacked layout and is not true of
               * this one; the exemption is retired in both files together rather than
               * left asserting the opposite of what the code does.
               *
               * So this REMOVES a divergence rather than creating one: the two halves of
               * the gift flow now date it identically. The minute is not lost — the same
               * handover carries its full timestamp in Provenance → "Owned since"
               * (`OwnerProvenanceCard`) the moment the gift is claimed, and nobody acts
               * on a pending gift's minute.
               */
              format: 'MMM do yyyy',
            })}
          </Typography>
        </Box>
      </Stack>
      {/* Same 14px / 8px-above / `#374151` the `descriptionText` slot rendered, so the
          INDENT is the only thing this change moves.

          The colour is written out because the class that carried it is gone. It was
          Tailwind's `gray-700` via the shared `Avatar`, and `stateCardTextSx` names that
          as this card family's description tone (`TransferInInfoSection` still renders
          the class). `text.primary` would be wrong in a way worth stating: it is the
          brand's own text colour, defaulting to `#000000`, which would put this sentence
          at the same weight as the recipient's NAME directly above it.

          Line-height 1.5, not Tailwind's `leading-5` (1.43) — §15, descriptive body copy
          gets air. That was previously forced onto the class by `stateCardTextSx`'s
          `!important` override; owning the element means owning it here instead. */}
      <Typography sx={{ fontSize: 14, lineHeight: 1.5, mt: 1, color: '#374151' }}>
        The recipient has not claimed the gift yet.
      </Typography>
    </Box>
  );
};
